import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DemoService } from 'src/app/services/demo/demo.service';
import { SchedulesService } from 'src/app/services/schedules';
import { AuthCompany } from 'src/models';

@Injectable({
  providedIn: 'root',
})
export class AttendancePlanService {
  constructor(
    private readonly schedulesService: SchedulesService,
    private readonly demoService: DemoService,
  ) {}

  /**
   * True when the company has upgraded but no work schedules exist yet.
   * Detects schedules via the work-schedules API (any persisted shift with an id).
   * Sample data skips setup only when the GrowthBook experiment allows mock data
   * on `/attendance`. The company `custom.showDemoData` setting alone does not.
   */
  async needsAttendanceSetup(company: AuthCompany): Promise<boolean> {
    if (!company || company.pricingPlan?.includes('basic')) {
      return false;
    }

    if (!company.companySettings?.workScheduleFeature) {
      return false;
    }

    if (await this.isAttendanceMockDataActive()) {
      return false;
    }

    return !(await this.hasAnyWorkSchedule());
  }

  /** Experiment gate first, then the rest of the attendance sample-data rules. */
  private async isAttendanceMockDataActive(): Promise<boolean> {
    const active = await firstValueFrom(
      this.demoService.showDemoDataOnRoute('/attendance').pipe(
        filter((value): value is boolean => value === true || value === false),
      ),
    );
    return active;
  }

  async hasAnyWorkSchedule(): Promise<boolean> {
    const now = DateTime.now();
    const from = now.minus({ months: 1 }).toISODate();
    const to = now.plus({ months: 3 }).toISODate();

    // Scope to everyone in the caller's report view (`all-on-reports`) rather than
    // letting the API self-default to the caller's own schedules. Without this a
    // manager who has no personal work schedule is judged as "needs setup" and is
    // redirected away from the Attendance report even though the users they manage
    // have active schedules (STAFF-145572).
    const page = await this.schedulesService.get({ from, to, limit: 1, user: 'all-self-on-reports' }).raw(0);

    return (page?.data ?? []).some(shift => !!shift?.id);
  }
}
